import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ConsoleLayout from "@/layouts/ConsoleLayout";
import MobileLayout from "@/layouts/MobileLayout";
import Login from "@/pages/Login";
import HeroLanding from "@/components/HeroLanding";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Sites from "@/pages/Sites";
import Guards from "@/pages/Guards";
import Firearms from "@/pages/Firearms";
import Reserved from "@/pages/Reserved";
import WageOrders from "@/pages/WageOrders";
import Tours from "@/pages/Tours";
import Schedule from "@/pages/Schedule";
import Attendance from "@/pages/Attendance";
import Incidents from "@/pages/Incidents";
import Payroll from "@/pages/Payroll";
import Benefits from "@/pages/Benefits";
import Invoices from "@/pages/Invoices";
import UsersPage from "@/pages/UsersPage";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import Docs from "@/pages/Docs";
import MobileHome from "@/pages/mobile/MobileHome";
import MobileReports from "@/pages/mobile/MobileReports";
import MobileAlerts from "@/pages/mobile/MobileAlerts";
import MobilePay from "@/pages/mobile/MobilePay";
import MobileProfile from "@/pages/mobile/MobileProfile";

function Protected({ children, mobile = false }) {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen grid place-items-center bg-background"><span className="font-mono text-xs text-muted-foreground" data-testid="app-loading">LOADING…</span></div>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (!mobile && user.role === "guard") return <Navigate to="/m" replace />;
  if (mobile && user.role !== "guard" && !["field_supervisor", "operations_manager", "scheduler", "system_admin"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/* Fleet standard #1. `/` used to be the protected console, so a visitor who was
 * not signed in was bounced to /login and the product had no front door at all.
 * Root now decides: signed out -> the hero; signed in -> the console (or the
 * mobile shell for a guard). A DEEP link stays unchanged -- /guards signed out
 * still goes to /login, because sending someone to a marketing page when they
 * asked for a screen loses their destination. */
function Root() {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen grid place-items-center bg-background"><span className="font-mono text-xs text-muted-foreground" data-testid="app-loading">LOADING…</span></div>;
  }
  if (user === false) return <Landing />;
  if (user.role === "guard") return <Navigate to="/m" replace />;
  return <ConsoleLayout />;
}

function Landing() {
  return (
    <HeroLanding
      eyebrow="Guard deployment & operations"
      title="Every post covered. Every hour accounted for."
      lede="AegisOps runs the detachment end to end — rosters and geofenced attendance, incident reports that reach the client, payroll hours your adviser can file, and invoices that foot from the shifts your guards actually worked."
      primaryCta={{ label: "Sign in", href: "/login" }}
      secondaryCta={{ label: "Field app", href: "/m" }}
    />
  );
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "guard" ? "/m" : "/"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/welcome" element={<Landing />} />
          <Route path="/" element={<Root />}>
            <Route index element={<Dashboard />} />
            <Route path="clients" element={<Clients />} />
            <Route path="sites" element={<Sites />} />
            <Route path="guards" element={<Guards />} />
            <Route path="firearms" element={<Firearms />} />
            <Route path="reserved" element={<Reserved />} />
            <Route path="wage-orders" element={<WageOrders />} />
            <Route path="tours" element={<Tours />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="incidents" element={<Incidents />} />
            <Route path="payroll" element={<Payroll />} />
            <Route path="benefits" element={<Benefits />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="settings" element={<Settings />} />
            <Route path="docs" element={<Docs />} />
          </Route>
          <Route path="/m" element={<Protected mobile><MobileLayout /></Protected>}>
            <Route index element={<MobileHome />} />
            <Route path="reports" element={<MobileReports />} />
            <Route path="alerts" element={<MobileAlerts />} />
            <Route path="pay" element={<MobilePay />} />
            <Route path="profile" element={<MobileProfile />} />
          </Route>
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" toastOptions={{ style: { borderRadius: 0, fontFamily: "'IBM Plex Sans', sans-serif" } }} />
    </AuthProvider>
  );
}

export default App;
